/** Public share entry points that require login, then open the destination. */
export const PENDING_DEEP_LINK_KEY = 'app:pendingDeepLink';

export type DeepLinkKind = 'chat' | 'topic';

const CHAT_PATH = /^\/chat\/([^/?#]+)$/;
const TOPIC_PATH = /^\/topics\/([^/?#]+)\/conversations$/;

function originBase(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://itoldyouso.app';
}

export function buildChatDeepLink(conversationId: string): string {
  return `${originBase()}/c/${encodeURIComponent(conversationId)}`;
}

export function buildTopicDeepLink(topicId: string): string {
  return `${originBase()}/t/${encodeURIComponent(topicId)}`;
}

export function destinationForDeepLink(
  kind: DeepLinkKind,
  id: string
): string | null {
  const trimmed = id.trim();
  if (!trimmed || trimmed.length > 80) return null;
  if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) return null;
  if (kind === 'chat') return `/chat/${trimmed}`;
  return `/topics/${trimmed}/conversations`;
}

/** Only allow in-app destinations we know how to open. */
export function isSafeDeepLinkPath(path: string): boolean {
  return CHAT_PATH.test(path) || TOPIC_PATH.test(path);
}

export function savePendingDeepLink(path: string): void {
  if (typeof window === 'undefined') return;
  if (!isSafeDeepLinkPath(path)) return;
  sessionStorage.setItem(PENDING_DEEP_LINK_KEY, path);
}

export function loadPendingDeepLink(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_DEEP_LINK_KEY)?.trim() ?? '';
  if (!raw || !isSafeDeepLinkPath(raw)) return null;
  return raw;
}

export function clearPendingDeepLink(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
}

export function buildDeepLinkShareText(
  kind: DeepLinkKind,
  url: string,
  label?: string
): string {
  if (kind === 'chat') {
    return label
      ? `Open this chat on iToldYouSo: ${label}\n${url}`
      : `Open this chat on iToldYouSo:\n${url}`;
  }
  return label
    ? `Open this topic on iToldYouSo: ${label}\n${url}`
    : `Open this topic on iToldYouSo:\n${url}`;
}
